import React, { FunctionComponent, useContext } from 'react'
import { AccessibilityRole, TextProps, TextStyle } from 'react-native'

import { includes } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import {
  FFM_COMPONENT_KEY,
  FFM_COMPONENT_PROP_KEY,
  FontFamilyMetadata,
  getFontFamilyFromMetadata,
  SUPPORTED_FONT_FAMILY,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/fontFamilyMetadata'

import { AccessibilityRolesNative } from '../../constants'
import { AppContext } from '../../context'
import {
  DisplaySize,
  FONT_FAMILY_AND_WEIGHT,
  getFontStylingFromMetadata,
  StyledText,
  TextAlign,
  TextSize,
  TextType,
} from '../../styles/typeStyles'

// @ts-ignore next-line
export interface BaseTextProps extends TextProps {
  text?: string | React.ReactNode
  size: TextSize
  type: TextType
  level?: string | null
  color?: string
  textAlign?: TextAlign
  style?: TextStyle
  bold?: boolean
  medium?: boolean
  /** Font weight regular */
  regular?: boolean
  /** Font weight light */
  light?: boolean
  testID?: string
  wrap?: boolean
  noRole?: boolean
  selectable?: boolean
  underline?: boolean
  role?: AccessibilityRolesNative
  nativeID?: string
}

type BaseTextContainerProps = {
  color: string
  textAlign: TextAlign
  wrap?: boolean
  bold?: boolean
  medium?: boolean
  regular?: boolean
  light?: boolean
  underline?: boolean
}

/**
 * Checks whether an explicit BaseText weight override should keep the Gazpacho display font.
 * The decision comes from the active product's display font metadata rather than its product type.
 */
const shouldUseGazpachoDisplayForWeightOverride = (
  fontFamilyMetadata: FontFamilyMetadata,
  type: TextType,
  size: TextSize,
): boolean => {
  // Only non-eyebrow display text can use Gazpacho. All other styles keep their existing weight-override behavior.
  if (type !== TextType.DISPLAY || size === DisplaySize.EYEBROW) {
    return false
  }

  // Resolve through metadata rather than checking the product type directly. BEND currently maps display to Gazpacho.
  const displayFontFamily = getFontFamilyFromMetadata(
    fontFamilyMetadata,
    FFM_COMPONENT_KEY.BASE_TEXT,
    FFM_COMPONENT_PROP_KEY.DISPLAY,
  )
  return displayFontFamily === SUPPORTED_FONT_FAMILY.GAZPACHO
}

const BaseTextContainer = styled(StyledText)<BaseTextContainerProps>(({
  theme,
  type,
  size,
  color,
  textAlign,
  bold,
  medium,
  regular,
  light,
  underline,
  wrap,
}) => {
  // Preserve the existing precedence when multiple weight props are provided: light > regular > medium > bold.
  const requestedFontWeight = light
    ? FONT_FAMILY_AND_WEIGHT.LIGHT
    : regular
      ? FONT_FAMILY_AND_WEIGHT.REGULAR
      : medium
        ? FONT_FAMILY_AND_WEIGHT.MEDIUM
        : bold
          ? FONT_FAMILY_AND_WEIGHT.BOLD
          : undefined
  const useGazpachoDisplayForWeightOverride =
    requestedFontWeight !== undefined
      ? shouldUseGazpachoDisplayForWeightOverride(theme.fontFamilyMetadata, type, size)
      : false

  return {
    color,
    textAlign,
    ...(requestedFontWeight !== undefined
      ? getFontStylingFromMetadata(
          theme.fontFamilyMetadata,
          requestedFontWeight,
          useGazpachoDisplayForWeightOverride ? FFM_COMPONENT_KEY.BASE_TEXT : undefined,
          useGazpachoDisplayForWeightOverride ? FFM_COMPONENT_PROP_KEY.DISPLAY : undefined,
        )
      : {}),
    ...(underline && { textDecorationLine: 'underline' }),
    ...(wrap && { flexShrink: 1, flexWrap: 'wrap' }),
  }
})

/**
 * A Heading component that encapsulates the typeStyle definitions
 * for font types. Refer to typeStyles for the correct heading styles
 */
export const BaseText: FunctionComponent<BaseTextProps> = ({
  text,
  size,
  type,
  level,
  color,
  textAlign = 'left',
  style,
  bold = false,
  medium = false,
  regular = false,
  light = false,
  underline = false,
  testID,
  wrap,
  noRole,
  children,
  role,
  selectable = true,
  nativeID,
  ...rest
}) => {
  const { copyPasteDisabled } = useContext(AppContext)
  const { colors } = useTheme()
  const accessibilityRole =
    (role as AccessibilityRole) ||
    (includes([TextType.DISPLAY, TextType.TITLE], type) && !noRole ? AccessibilityRolesNative.HEADER : undefined)
  const accessibilitylevel = accessibilityRole === AccessibilityRolesNative.HEADER && !noRole ? level : undefined
  return (
    <BaseTextContainer
      size={size}
      type={type}
      color={color || colors.textPrimary}
      textAlign={textAlign}
      style={style}
      underline={underline}
      // ignoring the next line because it looks like styled-components/native AccessibilityRole is different than the 0.68 react-native
      // @ts-ignore next-line
      accessibilityRole={accessibilityRole}
      // @ts-ignore next-line
      accessibilityLevel={accessibilitylevel}
      testID={testID}
      wrap={wrap}
      selectable={selectable && !copyPasteDisabled}
      bold={bold}
      medium={medium}
      regular={regular}
      light={light}
      nativeID={nativeID}
      {...rest}
    >
      {text}
      {children}
    </BaseTextContainer>
  )
}
